// ============================================================
// HALLUCINATION CODE (v8.357.0) - the pure half of engine/hallucination:
// the two condition codes, their bands, their lines, the per-round tick
// and the one-per-person merge. A LEAF: nothing here imports another
// engine module, so `conditions.ts` and `countdown.ts` may read it
// without the cycle `conditions → hallucination → afflictions →
// conditions` that the inducer's imports would close.
//
//   Hl<intensity>            STEP: the intensity falls one a round, gone at 0
//   Hl<intensity>:<rounds>   HOLD: the intensity stands, the rounds tick, gone at 0 (:0 = until removed)
//
// The lines say the INTENSITY and its band, never a vision - the GM
// supplies the vision, the sim the number.
// ============================================================
const HL_PREFIX = 'Hl';
export const HALLUCINATION_MAX = 10;

export interface Hallucination {
  /** 1..10 */
  intensity: number;
  /** HOLD mode when present (0 = until removed); STEP mode when null. */
  rounds: number | null;
}

export interface HallucinationBand {
  min: number;
  max: number;
  name: string;
  /** One line about the INTENSITY - never a specific vision. */
  phrase: string;
}

export const HALLUCINATION_BANDS: readonly HallucinationBand[] = [
  { min: 1, max: 2, name: 'faint', phrase: 'The edges of the world shimmer.' },
  { min: 3, max: 4, name: 'mild', phrase: 'Shapes move at the corner of the eye; there are voices.' },
  { min: 5, max: 6, name: 'moderate', phrase: 'Faces are wrong and the floor tilts.' },
  { min: 7, max: 8, name: 'severe', phrase: 'Little of what they see can be trusted.' },
  { min: 9, max: 9, name: 'overwhelming', phrase: 'Nothing they see can be trusted.' },
  { min: 10, max: 10, name: 'lost', phrase: 'They are somewhere else entirely.' },
];

export function bandOf(intensity: number): HallucinationBand {
  const i = Math.max(1, Math.min(HALLUCINATION_MAX, Math.round(intensity)));
  return HALLUCINATION_BANDS.find((b) => i >= b.min && i <= b.max) ?? HALLUCINATION_BANDS[0];
}

/** `Hl7` → step at 7; `Hl7:3` → hold at 7 for 3 rounds. Null for anything else. */
export function parseHallucination(code: string): Hallucination | null {
  const m = code.match(/^Hl(\d+)(?::(\d+))?$/);
  if (!m) return null;
  const intensity = parseInt(m[1], 10);
  if (intensity < 1 || intensity > HALLUCINATION_MAX) return null;
  return { intensity, rounds: m[2] !== undefined ? parseInt(m[2], 10) : null };
}

export function buildHallucinationCode(h: Hallucination): string {
  const i = Math.max(1, Math.min(HALLUCINATION_MAX, h.intensity));
  return h.rounds === null ? `${HL_PREFIX}${i}` : `${HL_PREFIX}${i}:${Math.max(0, h.rounds)}`;
}

export function isHallucinationCode(code: string): boolean {
  return /^Hl\d+(?::\d+)?$/.test(code);
}

/** 'Hallucinating 7 (severe)', 'Hallucinating 7 (severe, 3 rounds)', '… until removed)'. */
export function describeHallucination(code: string): string | null {
  const h = parseHallucination(code);
  if (!h) return null;
  const band = bandOf(h.intensity).name;
  if (h.rounds === null) return `Hallucinating ${h.intensity} (${band})`;
  if (h.rounds === 0) return `Hallucinating ${h.intensity} (${band}, until removed)`;
  return `Hallucinating ${h.intensity} (${band}, ${h.rounds} ${h.rounds === 1 ? 'round' : 'rounds'})`;
}

// ── The lines ─────────────────────────────────────────────────

export function narrateInduction(name: string, intensity: number): string {
  const b = bandOf(intensity);
  return `😵 ${name}'s senses swim - hallucinating, intensity ${intensity} (${b.name}). ${b.phrase}`;
}

export function narrateStacked(name: string, from: number, to: number): string {
  const b = bandOf(to);
  return to > from
    ? `😵 A second dose - ${name}'s hallucination climbs to ${to} (${b.name}).`
    : `😵 A second dose - ${name}'s hallucination holds at ${to} (${b.name}).`;
}

export function narrateExpiry(name: string): string {
  return `😵 The visions loosen their grip on ${name}.`;
}

/**
 * One round on: STEP falls a rung and says so; HOLD counts a round off.
 * Returns the next code (null = gone) and the line. Pure - the countdown
 * calls this for every `Hl` it finds.
 */
export function tickHallucination(code: string, name: string): { code: string | null; note: string } {
  const h = parseHallucination(code);
  if (!h) return { code, note: '' };
  if (h.rounds === null) {
    const next = h.intensity - 1;
    if (next <= 0) return { code: null, note: narrateExpiry(name) };
    return {
      code: buildHallucinationCode({ intensity: next, rounds: null }),
      note: `😵 ${name} is still hallucinating - ${next} (${bandOf(next).name}), down from ${h.intensity}.`,
    };
  }
  if (h.rounds === 0) return { code, note: '' }; // until removed: no tick
  const left = h.rounds - 1;
  if (left <= 0) return { code: null, note: narrateExpiry(name) };
  return {
    code: buildHallucinationCode({ intensity: h.intensity, rounds: left }),
    note: `😵 The visions hold ${name} - ${h.intensity} (${bandOf(h.intensity).name}), ${left} ${left === 1 ? 'round' : 'rounds'} left.`,
  };
}

export type HallucinationStacking = 'raise' | 'refresh' | 'extend';

/**
 * Put a hallucination on a list that may already hold one. ONE `Hl` per
 * person: a second dose RAISES (adds the new intensity, capped), REFRESHES
 * (the higher intensity, the new clock) or EXTENDS (the higher intensity,
 * the clocks added). Mutates like mergeCondition; returns what happened.
 */
export function mergeHallucination(
  conds: string[],
  incoming: Hallucination,
  stacking: HallucinationStacking,
  max = HALLUCINATION_MAX,
): { code: string; from: number | null; to: number } {
  const idx = conds.findIndex(isHallucinationCode);
  const cap = Math.max(1, Math.min(HALLUCINATION_MAX, max));
  if (idx < 0) {
    const code = buildHallucinationCode({ ...incoming, intensity: Math.min(cap, incoming.intensity) });
    conds.push(code);
    return { code, from: null, to: Math.min(cap, incoming.intensity) };
  }
  const had = parseHallucination(conds[idx])!;
  let intensity: number;
  let rounds: number | null;
  if (stacking === 'raise') {
    intensity = Math.min(cap, had.intensity + incoming.intensity);
    rounds = incoming.rounds;
  } else if (stacking === 'extend') {
    intensity = Math.min(cap, Math.max(had.intensity, incoming.intensity));
    rounds = had.rounds === null || incoming.rounds === null ? incoming.rounds : had.rounds + incoming.rounds;
  } else {
    intensity = Math.min(cap, Math.max(had.intensity, incoming.intensity));
    rounds = incoming.rounds;
  }
  const code = buildHallucinationCode({ intensity, rounds });
  conds.splice(idx, 1, code);
  return { code, from: had.intensity, to: intensity };
}
